import '@fontsource-variable/inter/wght.css';
import './assets/tailwind.css';
import './themes/theme.css';
import './shop/shop.css';
import './shop/account.css';
import './motion/motion.css';
import { createApp } from "vue";
import { installStaleTabReload } from "./utils/staleTab.js";
import App from "./App.vue";
import router from "./router";
import installMotion from "@/plugins/motion-ssr-fix.js"; // Import existing router
import { createPinia } from "pinia";
import { useColorStore } from "@/classes/useColorStore.js";
import { useBusinessStore } from "@/stores/businessStore.js"; // ✅ Import business store
import { usePageStore } from "@/stores/pageStore"; // ✅ Import page store
import { useHead } from "@vueuse/head";
import { watch } from "vue";
import * as Sentry from "@sentry/vue";
import {MotionPlugin} from "@vueuse/motion";
import { fitPhoto } from "@/utils/fitPhoto.js";
import { installPreviewBridge } from "@/classes/previewBridge.js";
import { REPLAY_OPTIONS, replayRates } from "@/classes/sentryOptions.js";

const app = createApp(App);
app.directive("fit-photo", fitPhoto);
const pinia = createPinia();


Sentry.init({
    app,
    environment: window.__SENTRY_ENV__ || import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || undefined,
    dsn: "https://66f32e17b98859367447e33a43a4d5e6@o4510200058871808.ingest.de.sentry.io/4510200695750736",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    integrations: [
        Sentry.replayIntegration(REPLAY_OPTIONS)
    ],
    sendDefaultPii: false,
    //tracePropagationTargets: ['localhost:3050', 'draksy.com', /^\//],
    ...replayRates(window.location.hostname),
});



if (import.meta.env.SSR) {
    console.log("Starting up...")
}

app.use(pinia);
app.use(router);
app.use(MotionPlugin);

// ✅ Restore SSR state
if (window.__SSR_STATE__) {
    // Read what the server sent before Pinia takes the object over: once it's
    // the store's state, its values are the stores' own refs.
    const ssrColors = {...(window.__SSR_STATE__.colorStore?.colors || {})};
    const ssrTheme = window.__SSR_STATE__.page?.pageData?.theme || 'glass';
    pinia.state.value = window.__SSR_STATE__; // ✅ Hydrate Pinia

    // ✅ Restore color store state
    const colorStore = useColorStore(pinia);
    if (!colorStore.colors || Object.keys(colorStore.colors).length === 0) {
        colorStore.setColors(ssrColors);
    }

    colorStore.setTheme(ssrTheme);

    // ✅ Restore business handle state
    const businessStore = useBusinessStore(pinia);
    if (window.__SSR_STATE__.businessStore?.handle) {
        if (typeof businessStore.setHandle === "function") {
            businessStore.setHandle(window.__SSR_STATE__.businessStore.handle);
        } else {
            businessStore.handle = window.__SSR_STATE__.businessStore.handle;
        }
    }
}

// Ensure router is ready before mounting
router.isReady().then(() => {
    app.mount("#app");
    installStaleTabReload();
    installPreviewBridge(router, pinia);

    // ✅ Dynamic Title Updates on Client
    const pageStore = usePageStore();

    watch(
        () => router.currentRoute.value.path, // ✅ Watches for route changes (not the query: ?back= changes nothing here)
        () => {
            if (!pageStore.pageData) return;

            const isHomePage = router.currentRoute.value.path === "/";
            useHead({
                title: isHomePage
                    ? pageStore.pageData.companyName
                    : `${pageStore.pageData.pageTitle || "Page"} - ${pageStore.pageData.companyName}`,
                meta: [
                    { name: "description", content: pageStore.pageData.metaDescription || "Default page description" },
                ],
            });
        },
        { immediate: true } // ✅ Runs on first load
    );
});
